/**
 * A student as the administrator's forms hold them: the patient form's fields,
 * as text, ready to validate and to send.
 *
 * The same record a mentor's own patient form makes, with the same required
 * fields — name, last name, email and phone — checked by the same rules.
 */

import { blankPersonDetails, validatePatient, type PersonDetails } from "../../patient";
import { whatsappNumberForForm } from "../../../shared/domain/whatsappNumber";
import type { CenterPatientDetail, StudentInput, StudentUpdate } from "../../center";

/** The patient form's person, as a centre's administrator fills it in. */
export type StudentPerson = PersonDetails;

/** The optional fields, sent as null when empty. */
export const OPTIONAL_STUDENT_FIELDS = [
    "pronouns",
    "date_of_birth",
    "sex",
    "street",
    "city",
    "state",
    "postal_code",
    "timezone",
] as const;

export function blankStudent(): StudentPerson {
    return blankPersonDetails();
}

/** Field → i18n key; empty when the student can be sent. */
export function validateStudent(person: StudentPerson): Record<string, string> {
    return validatePatient(person);
}

/** What the API takes for this person, in this course and with this mentor. */
export function toStudentInput(
    person: StudentPerson,
    placement: { course_id?: string; mentor_email: string; academic_year?: number | null; group: string | null }
): StudentInput {
    const optional = Object.fromEntries(
        OPTIONAL_STUDENT_FIELDS.map((field) => [field, person[field].trim() || null])
    ) as Pick<StudentInput, (typeof OPTIONAL_STUDENT_FIELDS)[number]>;
    return {
        first_name: person.first_name.trim(),
        last_name: person.last_name.trim(),
        contact_email: person.contact_email.trim(),
        contact_phone: person.contact_phone.trim(),
        whatsapp_phone: whatsappNumberForForm(person.whatsapp_phone, person.country || "ES") ?? null,
        country: person.country || "ES",
        ...optional,
        ...placement,
    };
}

/** The record the administrator opened, as the form holds it. */
export function studentFromDetail(detail: CenterPatientDetail): StudentPerson {
    const profile = detail.profile;
    return {
        first_name: detail.first_name,
        last_name: detail.last_name,
        contact_email: detail.contact_email ?? "",
        contact_phone: profile?.contact_phone ?? "",
        whatsapp_phone: profile?.whatsapp_phone ?? "",
        pronouns: profile?.pronouns ?? "",
        date_of_birth: profile?.date_of_birth ?? "",
        sex: profile?.sex ?? "",
        street: profile?.street ?? "",
        city: profile?.city ?? "",
        state: profile?.state ?? "",
        postal_code: profile?.postal_code ?? "",
        country: profile?.country ?? "ES",
        timezone: profile?.timezone ?? "",
    };
}

/** Only what changed between the record and the form: that is all an edit sends. */
export function studentChanges(before: StudentPerson, after: StudentPerson): StudentUpdate {
    const changes: Record<string, string | null> = {};
    (Object.keys(after) as (keyof StudentPerson)[]).forEach((field) => {
        const next = after[field].trim();
        if (next === before[field].trim()) return;
        if (field === "whatsapp_phone") {
            // Sent in international form, or null to clear it.
            changes.whatsapp_phone = whatsappNumberForForm(next, after.country || "ES") ?? null;
            return;
        }
        const optional = (OPTIONAL_STUDENT_FIELDS as readonly string[]).includes(field);
        changes[field] = optional && !next ? null : next;
    });
    return changes as StudentUpdate;
}
